import { useState } from 'react';
import {
  MapPin,
  Plus,
  RotateCcw,
  Eye,
  RefreshCw,
  X,
  Search,
  Sunrise,
  Sunset,
  GripVertical,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CircuitBuilderMap from '../components/assignment/CircuitBuilderMap';
import AssignmentStats from '../components/assignment/AssignmentStats';
import CircuitControlPanel from '../components/assignment/CircuitControlPanel';
import ConfirmationModal from '../components/ConfirmationModal';
import { useAssignmentManagement } from '../hooks/trAssignments';

const AssignmentManagement = () => {
  const [reinvertConfirmOpen, setReinvertConfirmOpen] = useState(false);

  const {
    loading,
    error,
    mode,
    setMode,
    stops,
    buses,
    drivers,
    routes,
    schools,
    selectedStops,
    optimizedOrder,
    inboundOrder,
    selectedBus,
    setSelectedBus,
    selectedDriver,
    setSelectedDriver,
    busParksAtHome,
    setBusParksAtHome,
    circuitName,
    setCircuitName,
    circuitDescription,
    setCircuitDescription,
    circuitCode,
    setCircuitCode,
    circuitStatus,
    setCircuitStatus,
    editingRouteId,
    isCreating,
    orderingModalOpen,
    orderingStep,
    draftOutboundOrder,
    draftInboundOrder,
    dragInsertIndex,
    setDragInsertIndex,
    coherenceResult,
    viewModalRoute,
    viewModalStopsSorted,
    viewModalLoading,
    viewModalError,
    viewModalSearch,
    setViewModalSearch,
    viewDirection,
    setViewDirection,
    deleteModalOpen,
    routeToDelete,
    deleteLoading,
    dynamicStats,
    loadData,
    resetForm,
    handleStopSelection,
    handleCreateCircuit,
    openOrderingModal,
    closeOrderingModal,
    goToOrderingStep2,
    goBackToStep1,
    resetEveningToInverse,
    handleDraftReorder,
    validateOrderingModal,
    handleViewRoute,
    closeViewModal,
    handleEditRoute,
    handleDeleteRoute,
    confirmDeleteRoute,
    onDeleteClose,
  } = useAssignmentManagement();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des donnees d&apos;assignation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-full">
      {/* Header */}
      <div className="px-4 py-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Assignations</h1>
            <p className="text-gray-600">
              {mode === 'create' ? 'Création rapide de circuit' : "Vue d'ensemble des assignations"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
            {mode === 'create' && (
              <Button variant="secondary" className="hidden md:inline-flex" onClick={resetForm}>
                <RotateCcw className="w-4 h-4" />
                Réinitialiser
              </Button>
            )}
            <Button
              className="hidden md:inline-flex"
              onClick={() => {
                if (mode === 'create') {
                  setMode('overview');
                } else {
                  resetForm();
                  setMode('create');
                }
              }}
            >
              {mode === 'create' ? (
                <>
                  <Eye className="w-4 h-4" />
                  Vue d&apos;ensemble
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Nouveau Circuit
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className={`flex-1 ${mode === 'create' ? 'flex flex-col xl:flex-row min-h-0' : ''}`}>
        {mode === 'overview' ? (
          <AssignmentStats
            stats={dynamicStats}
            routes={routes}
            buses={buses}
            drivers={drivers}
            stops={stops}
            onViewRoute={handleViewRoute}
            onEditRoute={handleEditRoute}
            onDeleteRoute={handleDeleteRoute}
            error={error}
            onRetry={loadData}
          />
        ) : (
          <>
            <div className="h-[280px] xl:h-auto xl:flex-1 xl:min-h-[600px] relative">
              <CircuitBuilderMap
                stops={[
                  ...(schools.length > 0
                    ? [
                        {
                          ...schools[0],
                          id: `school-${schools[0].id}`,
                          name: schools[0].name,
                          stop_name: schools[0].name,
                          address: schools[0].address || '',
                          latitude: schools[0].latitude,
                          longitude: schools[0].longitude,
                          student_count: 0,
                          isSchool: true,
                          assignmentStatus: 'available',
                          isAssigned: false,
                          assignedToRoutes: [],
                        },
                      ]
                    : []),
                  ...stops,
                ]}
                selectedStops={selectedStops}
                optimizedOrder={optimizedOrder}
                inboundOrder={inboundOrder}
                onStopSelection={handleStopSelection}
              />
            </div>
            <div className="w-full xl:w-80 bg-white border-t xl:border-t-0 xl:border-l shadow-lg xl:flex-shrink-0">
              <CircuitControlPanel
                selectedStops={selectedStops}
                optimizedOrder={optimizedOrder}
                inboundOrder={inboundOrder}
                buses={buses}
                drivers={drivers}
                selectedBus={selectedBus}
                selectedDriver={selectedDriver}
                busParksAtHome={busParksAtHome}
                circuitName={circuitName}
                circuitDescription={circuitDescription}
                circuitCode={circuitCode}
                circuitStatus={circuitStatus}
                isCreating={isCreating}
                editingRouteId={editingRouteId}
                onOpenOrderingModal={openOrderingModal}
                onBusChange={setSelectedBus}
                onDriverChange={setSelectedDriver}
                onBusParksAtHomeChange={setBusParksAtHome}
                onNameChange={setCircuitName}
                onDescriptionChange={setCircuitDescription}
                onCodeChange={setCircuitCode}
                onStatusChange={setCircuitStatus}
                onCreateCircuit={handleCreateCircuit}
              />
            </div>
          </>
        )}
      </div>

      {/* Modal Vue circuit */}
      {viewModalRoute && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={closeViewModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
              <div className="relative border-b border-gray-200 px-4 pb-4 pt-4 pr-14">
                <button
                  type="button"
                  onClick={closeViewModal}
                  className="absolute right-2 top-2 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold leading-snug text-gray-900">Arrêts du circuit</h2>
                    <p
                      className="mt-0.5 truncate text-sm text-gray-600"
                      title={viewModalRoute.route_name ?? viewModalRoute.name ?? ''}
                    >
                      {viewModalRoute.route_name ?? viewModalRoute.name ?? 'Circuit'}
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-1.5 lg:w-auto lg:items-end">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Ordre des arrêts</span>
                    <div
                      className="inline-flex w-full max-w-md rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100/90 p-1 shadow-sm lg:w-auto"
                      role="group"
                    >
                      <button
                        type="button"
                        onClick={() => setViewDirection('inbound')}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-all sm:flex-initial sm:px-4 ${viewDirection === 'inbound' ? 'bg-white text-blue-700 shadow-md ring-1 ring-blue-200/60' : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'}`}
                      >
                        <Sunrise className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Aller</span>
                        <span className="hidden text-xs font-normal opacity-80 sm:inline">matin</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewDirection('outbound')}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-all sm:flex-initial sm:px-4 ${viewDirection === 'outbound' ? 'bg-white text-indigo-700 shadow-md ring-1 ring-indigo-200/60' : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'}`}
                      >
                        <Sunset className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Retour</span>
                        <span className="hidden text-xs font-normal opacity-80 sm:inline">soir</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto flex flex-col p-4">
                {viewModalLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <span className="ml-3 text-gray-600">Chargement des arrêts...</span>
                  </div>
                ) : viewModalError ? (
                  <p className="text-red-600 text-center py-6">{viewModalError}</p>
                ) : viewModalStopsSorted.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">Aucun arrêt assigné à ce circuit.</p>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Rechercher par nom ou adresse..."
                          value={viewModalSearch}
                          onChange={(e) => setViewModalSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        {viewModalSearch && (
                          <button
                            type="button"
                            onClick={() => setViewModalSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Effacer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const q = viewModalSearch.trim().toLowerCase();
                      const filtered = viewModalStopsSorted.filter((item) => {
                        if (!q) return true;
                        const stop = item.stop ?? item;
                        return (
                          (stop.stop_name ?? stop.name ?? '').toLowerCase().includes(q) ||
                          (stop.address ?? stop.adresse ?? '').toLowerCase().includes(q)
                        );
                      });
                      if (filtered.length === 0)
                        return (
                          <p className="text-gray-500 text-center py-6">Aucun arrêt ne correspond à la recherche.</p>
                        );
                      return (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {filtered.map((item, index) => {
                            const stop = item.stop ?? item;
                            const name = stop.stop_name ?? stop.name ?? '—';
                            const address = stop.address ?? stop.adresse ?? '—';
                            const lat = stop.latitude ?? stop.lat ?? '';
                            const lng = stop.longitude ?? stop.lng ?? stop.lon ?? '';
                            const order =
                              viewDirection === 'outbound'
                                ? (item.outbound_order ?? item.order ?? index + 1)
                                : (item.inbound_order ?? item.order ?? index + 1);
                            return (
                              <div
                                key={item.id ?? stop.id ?? index}
                                className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <span className="inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                                    {order}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={name}>
                                      {name}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-600 line-clamp-2" title={address}>
                                      {address}
                                    </p>
                                    {lat !== '' && lng !== '' && (
                                      <p className="mt-2 text-xs text-gray-500 font-mono">
                                        {lat}, {lng}
                                      </p>
                                    )}
                                  </div>
                                  <MapPin className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
              <div className="px-4 py-4 border-t border-gray-200 flex justify-end">
                <button
                  type="button"
                  onClick={closeViewModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ordonnancement */}
      {orderingModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={closeOrderingModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="relative border-b border-gray-200 px-4 pb-4 pt-4 pr-14">
                <button
                  type="button"
                  onClick={closeOrderingModal}
                  className="absolute right-2 top-2 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex flex-col gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">Ordonner les arrêts</h2>

                  {/* Indicateur d'étapes — identique création et édition */}
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${orderingStep === 1 ? 'bg-blue-100 text-blue-700' : 'bg-green-50 text-green-700'}`}
                    >
                      <Sunrise className="w-3.5 h-3.5 shrink-0" />
                      <span>Matin</span>
                    </div>
                    <div className="flex-1 h-px bg-gray-200" />
                    <div
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${orderingStep === 2 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}
                    >
                      <Sunset className="w-3.5 h-3.5 shrink-0" />
                      <span>Soir</span>
                    </div>
                  </div>

                  {/* Indications contextuelles */}
                  {orderingStep === 2 && (
                    <p className="text-xs text-gray-500">Glissez pour ajuster l&apos;ordre du soir.</p>
                  )}
                  {orderingStep === 2 && busParksAtHome && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      Le bus rentre chez le chauffeur — ajustez l&apos;ordre du soir librement.
                    </p>
                  )}
                </div>
              </div>

              {/* Liste drag-and-drop */}
              <div
                className="flex-1 overflow-y-auto p-4 space-y-0"
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) setDragInsertIndex(null);
                }}
              >
                {(() => {
                  const visible = (orderingStep === 2 ? draftOutboundOrder : draftInboundOrder).filter(
                    (s) => !s.isSchool,
                  );
                  return visible.map((stop, index) => (
                    <div key={`${orderingStep}-${stop.id}`}>
                      <div
                        className={`h-0.5 rounded-full mx-1 my-0.5 transition-all duration-100 ${dragInsertIndex === index ? 'bg-blue-500 shadow-[0_0_4px_rgba(59,130,246,0.6)]' : 'bg-transparent'}`}
                      />
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', String(index));
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => setDragInsertIndex(null)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          const rect = e.currentTarget.getBoundingClientRect();
                          const insertion = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
                          if (dragInsertIndex !== insertion) setDragInsertIndex(insertion);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const src = Number(e.dataTransfer.getData('text/plain'));
                          const rect = e.currentTarget.getBoundingClientRect();
                          const ip = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
                          const target = ip <= src ? ip : ip - 1;
                          setDragInsertIndex(null);
                          handleDraftReorder(src, target);
                        }}
                        className="rounded-lg border px-3 py-3 flex items-center gap-3 cursor-grab active:cursor-grabbing select-none transition-colors duration-100 border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm my-1"
                      >
                        <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-800 truncate block">
                            {stop.name || stop.stop_name || 'Arrêt'}
                          </span>
                          {stop.students?.length > 0 && (
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              {stop.students
                                .map((s) => [s.first_name, s.last_name].filter(Boolean).join(' '))
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                        <MapPin className="w-4 h-4 text-gray-300 shrink-0" />
                      </div>
                      {index === visible.length - 1 && (
                        <div
                          className={`h-0.5 rounded-full mx-1 my-0.5 transition-all duration-100 ${dragInsertIndex === visible.length ? 'bg-blue-500 shadow-[0_0_4px_rgba(59,130,246,0.6)]' : 'bg-transparent'}`}
                        />
                      )}
                    </div>
                  ));
                })()}
              </div>

              {/* Bandeau erreurs de cohérence */}
              {coherenceResult?.errors?.length > 0 && (
                <div className="px-4 py-3 bg-red-50 border-t border-red-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700 space-y-0.5">
                    {coherenceResult.errors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer — identique création et édition */}
              <div className="px-4 py-4 border-t border-gray-200 flex items-center gap-2">
                {orderingStep === 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={closeOrderingModal}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                    <div className="flex-1" />
                    <Button type="button" onClick={goToOrderingStep2}>
                      Continuer <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={goBackToStep1}
                      className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <div className="flex-1" />
                    <Button type="button" variant="outline" onClick={() => setReinvertConfirmOpen(true)}>
                      <RotateCcw className="w-4 h-4 mr-1.5" /> Repartir de l&apos;inversion
                    </Button>
                    <Button type="button" onClick={validateOrderingModal}>
                      Valider
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation réinversion soir */}
      <ConfirmationModal
        isOpen={reinvertConfirmOpen}
        onClose={() => setReinvertConfirmOpen(false)}
        onConfirm={() => {
          resetEveningToInverse();
          setReinvertConfirmOpen(false);
        }}
        title="Repartir de l'inversion"
        message="Cela remplacera l'ordre du soir par l'inverse exact de l'ordre du matin. Vos ajustements actuels seront perdus."
        confirmText="Confirmer"
        cancelText="Annuler"
        type="warning"
        loading={false}
      />

      {/* Modal suppression */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={onDeleteClose}
        onConfirm={confirmDeleteRoute}
        title="Supprimer le circuit"
        message={`Êtes-vous sûr de vouloir supprimer le circuit "${routeToDelete?.route_name || routeToDelete?.name || ''}" ?\n\nCette action supprimera également tous les arrêts assignés à ce circuit.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AssignmentManagement;
